/**
 * Inside Sales — read-only customer browse (master–detail layout like Batch details).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Button } from "@/components/ui/Button";
import {
  FreshdeskCompanyAutocomplete,
  type FreshdeskCompanySelection,
} from "@/components/ui/FreshdeskCompanyAutocomplete";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import {
  fetchInsideSalesClientDetail,
  fetchInsideSalesClientList,
  createInsideSalesCustomerComplaint,
  createInsideSalesCustomerRma,
  saveCustomerFreshdeskCompany,
  type CreateCustomerComplaintInput,
  fetchInsideSalesCustomerRelated,
  fetchInsideSalesCustomerTickets,
  INSIDE_SALES_CUSTOMER_LIST_PAGE_SIZE,
  type FreshsalesAccountDisplayField,
  type InsideSalesClientListRow,
  type CreateCustomerRmaInput,
  type InsideSalesClientRepBadge,
} from "@/lib/insideSalesClientData";
import { CustomerNewComplaintModal, CustomerNewRmaModal } from "./CustomerNewRmaModal";
import { buildShippingGosiDetailsPath } from "@/lib/hubEntityDetailPaths";
import { cn } from "@/lib/utils";

type CustomerDetailTab = "details" | "orders" | "invoices" | "devices" | "complaints" | "rmas" | "tickets";

const CUSTOMER_DETAIL_TABS: { key: CustomerDetailTab; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "orders", label: "Orders" },
  { key: "invoices", label: "Invoices" },
  { key: "devices", label: "Devices" },
  { key: "complaints", label: "Complaints" },
  { key: "rmas", label: "RMAs" },
  { key: "tickets", label: "Tickets" },
];

function freshdeskStatusLabel(status: number | null): string {
  if (status === 2) return "Open";
  if (status === 3) return "Pending";
  if (status === 4) return "Resolved";
  if (status === 5) return "Closed";
  return status == null ? "—" : String(status);
}

function displayText(value: unknown): string {
  if (value == null) {
    return "—";
  }
  const text = String(value).trim();
  return text === "" ? "—" : text;
}

function linkedPortalOrgDisplay(row: Record<string, unknown>): ReactNode {
  const name = row.wireless_portal_org_name == null ? "" : String(row.wireless_portal_org_name).trim();
  if (name !== "") {
    return name;
  }
  return <span className="text-slate-600">No portal org (Freshsales match)</span>;
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{value}</dd>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</dl>
    </section>
  );
}

function MagnetExemptionStatus({ exempt }: { exempt: unknown }) {
  const isExempt = exempt === true;
  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold",
        isExempt
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-slate-200 bg-slate-50 text-slate-700",
      )}
    >
      {isExempt ? "Exempt" : "Not exempt"}
    </span>
  );
}

function parseRepBadge(value: unknown): InsideSalesClientRepBadge | null {
  if (value == null || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const displayName = record.display_name == null ? "" : String(record.display_name).trim();
  if (displayName === "") {
    return null;
  }
  return {
    display_name: displayName,
    email: record.email == null || String(record.email).trim() === "" ? null : String(record.email).trim(),
    avatar_url:
      record.avatar_url == null || String(record.avatar_url).trim() === ""
        ? null
        : String(record.avatar_url).trim(),
  };
}

function SalesRepBadge({ rep }: { rep: InsideSalesClientRepBadge }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
      <HubUserAvatar avatarUrl={rep.avatar_url} label={rep.display_name} size="sm" className="h-5 w-5 text-[10px]" />
      <span className="truncate">{rep.display_name}</span>
    </span>
  );
}

function formatCustomerDate(value: string | null): string {
  if (value == null || value.trim() === "") return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ClientDetailPanel({
  row,
  freshsalesFields,
  freshdeskCompany,
  freshdeskSaving,
  onFreshdeskCompanyChange,
}: {
  row: Record<string, unknown>;
  freshsalesFields: FreshsalesAccountDisplayField[];
  freshdeskCompany: FreshdeskCompanySelection | null;
  freshdeskSaving: boolean;
  onFreshdeskCompanyChange: (company: FreshdeskCompanySelection | null) => void;
}) {
  const ownerRep = parseRepBadge(row.owner_rep);
  const responsibleRep = parseRepBadge(row.responsible_rep);
  const billToLines = [
    row.bill_to_address_line_1,
    row.bill_to_address_line_2,
    row.bill_to_address_line_3,
  ]
    .map((line) => (line == null ? "" : String(line).trim()))
    .filter((line) => line !== "");
  const shipToLines = [
    row.ship_to_address_line_1,
    row.ship_to_address_line_2,
    row.ship_to_address_line_3,
  ]
    .map((line) => (line == null ? "" : String(line).trim()))
    .filter((line) => line !== "");

  return (
    <div className="space-y-6">
      <DetailSection title="Freshdesk">
        <DetailField
          label="Freshdesk company"
          value={
            <FreshdeskCompanyAutocomplete
              id="customer-freshdesk-company"
              company={freshdeskCompany}
              disabled={freshdeskSaving}
              onCompanyChange={onFreshdeskCompanyChange}
            />
          }
        />
      </DetailSection>

      <DetailSection title="Freshsales">
        <DetailField label="Freshsales account id" value={displayText(row.fs_account_id)} />
        <DetailField label="Account name" value={displayText(row.fs_account_name)} />
        <DetailField label="Customer category" value={displayText(row.sector)} />
        <DetailField label="Stocking distributor" value={displayText(row.stocking)} />
        <DetailField label="Rep agency" value={displayText(row.rep_agency)} />
        <DetailField label="Tax exemption date" value={displayText(row.tax_exemption_date)} />
        <DetailField
          label="Account owner"
          value={ownerRep != null ? <SalesRepBadge rep={ownerRep} /> : "—"}
        />
        <DetailField
          label="Responsible sales rep"
          value={responsibleRep != null ? <SalesRepBadge rep={responsibleRep} /> : "—"}
        />
        {freshsalesFields.map((field) => (
          <DetailField key={field.field_key} label={field.label} value={displayText(field.value)} />
        ))}
      </DetailSection>

      <DetailSection title="Charge exemptions">
        <DetailField
          label="Shipping fee"
          value={<MagnetExemptionStatus exempt={row.shipping_fee_exempt} />}
        />
        <DetailField
          label="MG-5A"
          value={<MagnetExemptionStatus exempt={row.magnet_exempt_mg_5a} />}
        />
        <DetailField
          label="MG-3C-2"
          value={<MagnetExemptionStatus exempt={row.magnet_exempt_mg_3c_2} />}
        />
      </DetailSection>

      <DetailSection title="Megaventory client">
        <DetailField label="Megaventory client name" value={displayText(row.mv_client_name)} />
        <DetailField label="Client type" value={displayText(row.mv_client_type)} />
        <DetailField label="Bill-to recipient" value={displayText(row.bill_to_recipient)} />
        <DetailField
          label="Bill-to address"
          value={
            billToLines.length > 0 ? (
              <span className="whitespace-pre-wrap">
                {[...billToLines, row.bill_to_city, row.bill_to_state, row.bill_to_zip, row.bill_to_country]
                  .map((part) => (part == null ? "" : String(part).trim()))
                  .filter((part) => part !== "")
                  .join("\n")}
              </span>
            ) : (
              "—"
            )
          }
        />
        <DetailField label="Phone" value={displayText(row.mv_client_phone)} />
        <DetailField label="Email" value={displayText(row.mv_client_email)} />
        <DetailField label="Tax ID" value={displayText(row.mv_client_tax_id)} />
      </DetailSection>

      <DetailSection title="Megaventory end user">
        <DetailField label="Megaventory end user name" value={displayText(row.mv_end_user_name)} />
        <DetailField label="Linked portal org" value={linkedPortalOrgDisplay(row)} />
        <DetailField label="Ship-to recipient" value={displayText(row.ship_to_recipient)} />
        <DetailField
          label="Ship-to address"
          value={
            shipToLines.length > 0 ? (
              <span className="whitespace-pre-wrap">
                {[...shipToLines, row.ship_to_city, row.ship_to_state, row.ship_to_zip, row.ship_to_country]
                  .map((part) => (part == null ? "" : String(part).trim()))
                  .filter((part) => part !== "")
                  .join("\n")}
              </span>
            ) : (
              "—"
            )
          }
        />
        <DetailField label="Contact phone" value={displayText(row.mv_contact_phone)} />
        <DetailField label="Contact email" value={displayText(row.mv_contact_email)} />
      </DetailSection>
    </div>
  );
}

function CustomerTicketsPanel({
  query,
}: {
  query: {
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    data:
      | {
          linked: boolean;
          tickets: { id: number; subject: string | null; status: number | null; created_at: string | null; url: string }[];
        }
      | undefined;
  };
}) {
  if (query.isLoading) {
    return <p className="text-sm text-slate-600">Loading tickets…</p>;
  }
  if (query.isError) {
    return (
      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
        {query.error?.message ?? "Could not load Freshdesk tickets."}
      </p>
    );
  }
  if (query.data == null) {
    return null;
  }
  if (!query.data.linked) {
    return <p className="px-4 py-6 text-sm text-slate-600">No Freshdesk company is linked to this customer.</p>;
  }
  const columns: HubDataTableColumn<(typeof query.data.tickets)[number]>[] = [
    {
      id: "subject",
      header: "Ticket",
      sortValue: (row) => row.subject ?? "",
      cell: (row) => (
        <a className="font-medium text-brand-700 hover:text-brand-800" href={row.url} target="_blank" rel="noreferrer">
          {displayText(row.subject)}
        </a>
      ),
    },
    {
      id: "status",
      header: "Status",
      sortValue: (row) => freshdeskStatusLabel(row.status),
      cell: (row) => freshdeskStatusLabel(row.status),
    },
    {
      id: "created",
      header: "Created",
      sortValue: (row) => row.created_at ?? "",
      cell: (row) => formatCustomerDate(row.created_at),
    },
  ];
  return (
    <HubDataTable
      columns={columns}
      rows={query.data.tickets}
      rowKey={(row) => row.id}
      fillViewport={false}
      className="max-h-none flex-1 rounded-none border-0 shadow-none"
      empty={<span>No Freshdesk tickets.</span>}
      defaultSortKey="created"
      defaultSortDir="desc"
    />
  );
}

function CustomerRelatedPanel({
  tab,
  query,
}: {
  tab: Exclude<CustomerDetailTab, "details" | "tickets">;
  query: {
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    data:
      | {
          orders: { id: number; number: string | null; status: string | null }[];
          invoices: { mv_id: number; number: string | null; status: string | null; date_created: string | null }[];
          devices: { id: number; device_label_serial: string }[];
          complaints: { id: number; title: string; status: string | null; created_at: string | null }[];
          rmas: { id: number; rma_number: string | null; status: string | null; complaint: string | null; requested_at: string | null }[];
        }
      | undefined;
  };
}) {
  const data = query.data;
  const tableClassName = "max-h-none flex-1 rounded-none border-0 shadow-none";
  if (tab === "orders") {
    const columns: HubDataTableColumn<NonNullable<typeof data>["orders"][number]>[] = [
      {
        id: "number",
        header: "Order",
        sortValue: (row) => row.number ?? "",
        cell: (row) => (
          <Link
            className="font-medium text-brand-700 hover:text-brand-800"
            to={`/inside-sales/sales-orders?search=${encodeURIComponent(row.number ?? String(row.id))}`}
          >
            {displayText(row.number)}
          </Link>
        ),
      },
      { id: "status", header: "Status", cell: (row) => displayText(row.status) },
    ];
    return (
      <HubDataTable
        columns={columns}
        rows={data?.orders ?? []}
        rowKey={(row) => row.id}
        fillViewport={false}
        className={tableClassName}
        loading={query.isLoading}
        error={query.isError ? query.error?.message ?? "Could not load this list." : undefined}
        empty={<span>No orders.</span>}
      />
    );
  }
  if (tab === "invoices") {
    const columns: HubDataTableColumn<NonNullable<typeof data>["invoices"][number]>[] = [
      {
        id: "number",
        header: "Invoice",
        sortValue: (row) => row.number ?? "",
        cell: (row) => (
          <Link className="font-medium text-brand-700 hover:text-brand-800" to={buildShippingGosiDetailsPath(row.mv_id, row.number)}>
            {displayText(row.number)}
          </Link>
        ),
      },
      {
        id: "date",
        header: "Date",
        sortValue: (row) => row.date_created ?? "",
        cell: (row) => formatCustomerDate(row.date_created),
      },
      { id: "status", header: "Status", cell: (row) => displayText(row.status) },
    ];
    return (
      <HubDataTable
        columns={columns}
        rows={data?.invoices ?? []}
        rowKey={(row) => row.mv_id}
        fillViewport={false}
        className={tableClassName}
        loading={query.isLoading}
        error={query.isError ? query.error?.message ?? "Could not load this list." : undefined}
        empty={<span>No invoices.</span>}
        defaultSortKey="date"
        defaultSortDir="desc"
      />
    );
  }
  if (tab === "devices") {
    const columns: HubDataTableColumn<NonNullable<typeof data>["devices"][number]>[] = [
      {
        id: "serial",
        header: "Serial",
        cell: (row) => <span className="font-mono">{row.device_label_serial}</span>,
        sortValue: (row) => row.device_label_serial,
      },
    ];
    return (
      <HubDataTable
        columns={columns}
        rows={data?.devices ?? []}
        rowKey={(row) => row.id}
        fillViewport={false}
        className={tableClassName}
        loading={query.isLoading}
        error={query.isError ? query.error?.message ?? "Could not load this list." : undefined}
        empty={<span>No devices.</span>}
      />
    );
  }
  if (tab === "complaints") {
    const columns: HubDataTableColumn<NonNullable<typeof data>["complaints"][number]>[] = [
      {
        id: "created",
        header: "Created",
        sortValue: (row) => row.created_at ?? "",
        cell: (row) => formatCustomerDate(row.created_at),
      },
      { id: "summary", header: "Summary", cell: (row) => row.title },
      { id: "status", header: "Status", cell: (row) => displayText(row.status) },
    ];
    return (
      <HubDataTable
        columns={columns}
        rows={data?.complaints ?? []}
        rowKey={(row) => row.id}
        fillViewport={false}
        className={tableClassName}
        loading={query.isLoading}
        error={query.isError ? query.error?.message ?? "Could not load this list." : undefined}
        empty={<span>No complaints.</span>}
        defaultSortKey="created"
        defaultSortDir="desc"
      />
    );
  }
  const columns: HubDataTableColumn<NonNullable<typeof data>["rmas"][number]>[] = [
    {
      id: "number",
      header: "RMA",
      sortValue: (row) => row.rma_number ?? "",
      cell: (row) => (
        <Link className="font-medium text-brand-700 hover:text-brand-800" to={`/quality/rmas/${String(row.id)}`}>
          {displayText(row.rma_number)}
        </Link>
      ),
    },
    {
      id: "requested",
      header: "Requested",
      sortValue: (row) => row.requested_at ?? "",
      cell: (row) => formatCustomerDate(row.requested_at),
    },
    { id: "status", header: "Status", cell: (row) => displayText(row.status) },
    { id: "complaint", header: "Complaint", cell: (row) => row.complaint ?? "—" },
  ];
  return (
    <HubDataTable
      columns={columns}
      rows={data?.rmas ?? []}
      rowKey={(row) => row.id}
      fillViewport={false}
      className={tableClassName}
      loading={query.isLoading}
      error={query.isError ? query.error?.message ?? "Could not load this list." : undefined}
      empty={<span>No RMAs.</span>}
      defaultSortKey="requested"
      defaultSortDir="desc"
    />
  );
}

export interface InsideSalesClientBrowsePageProps {
  title: string;
  help: string;
  searchLabel: string;
  searchPlaceholder: string;
  emptySelectionMessage: string;
}

export function InsideSalesClientBrowsePage({
  title,
  help,
  searchLabel,
  searchPlaceholder,
  emptySelectionMessage,
}: InsideSalesClientBrowsePageProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fsAccountIdParam = searchParams.get("fs_account_id") ?? "";
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  type ClientSortKey = "account" | "owner";
  const [sortKey, setSortKey] = useState<ClientSortKey>("account");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");
  const [detailTab, setDetailTab] = useState<CustomerDetailTab>("details");
  const [createKind, setCreateKind] = useState<"complaint" | "rma" | null>(null);
  const queryClient = useQueryClient();

  const selectedFsAccountId = useMemo(() => {
    const parsed = Number.parseInt(fsAccountIdParam.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [fsAccountIdParam]);

  useEffect(() => {
    setDetailTab("details");
  }, [selectedFsAccountId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchText]);

  const listQuery = useInfiniteQuery({
    queryKey: ["inside-sales", "customers", "list", debouncedSearch],
    queryFn: ({ pageParam }) =>
      fetchInsideSalesClientList({
        query: debouncedSearch,
        limit: INSIDE_SALES_CUSTOMER_LIST_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.rows.length, 0);
      if (loadedCount >= lastPage.total_count || lastPage.rows.length === 0) {
        return undefined;
      }
      return loadedCount;
    },
  });
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const detailQuery = useQuery({
    queryKey: ["inside-sales", "customers", "detail", selectedFsAccountId],
    queryFn: () => fetchInsideSalesClientDetail(selectedFsAccountId as number),
    enabled: selectedFsAccountId != null,
  });

  const relatedQuery = useQuery({
    queryKey: ["inside-sales", "customers", "related", selectedFsAccountId],
    queryFn: () => fetchInsideSalesCustomerRelated(selectedFsAccountId as number),
    enabled: selectedFsAccountId != null && detailTab !== "details" && detailTab !== "tickets",
  });

  const ticketsQuery = useQuery({
    queryKey: ["inside-sales", "customers", "tickets", selectedFsAccountId],
    queryFn: () => fetchInsideSalesCustomerTickets(selectedFsAccountId as number),
    enabled: selectedFsAccountId != null && detailTab === "tickets",
  });

  const saveFreshdeskCompany = useMutation({
    mutationFn: (freshdeskCompanyId: number | null) =>
      saveCustomerFreshdeskCompany(selectedFsAccountId as number, freshdeskCompanyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["inside-sales", "customers", "detail", selectedFsAccountId],
      });
    },
  });

  const createComplaint = useMutation({
    mutationFn: (input: CreateCustomerComplaintInput) =>
      createInsideSalesCustomerComplaint(selectedFsAccountId as number, input),
    onSuccess: () => {
      setCreateKind(null);
      void queryClient.invalidateQueries({
        queryKey: ["inside-sales", "customers", "related", selectedFsAccountId],
      });
    },
  });
  const createRma = useMutation({
    mutationFn: (input: CreateCustomerRmaInput) =>
      createInsideSalesCustomerRma(selectedFsAccountId as number, input),
    onSuccess: (created) => {
      setCreateKind(null);
      void queryClient.invalidateQueries({
        queryKey: ["inside-sales", "customers", "related", selectedFsAccountId],
      });
      void navigate(`/quality/rmas/${String(created.id)}`);
    },
  });

  const rows = listQuery.data?.pages.flatMap((page) => page.rows) ?? [];
  const totalCount = listQuery.data?.pages[0]?.total_count;
  const selectedListRow = rows.find((row) => row.fs_account_id === selectedFsAccountId) ?? null;

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const root = listScrollRef.current;
    if (sentinel == null || root == null) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        if (!listQuery.hasNextPage || listQuery.isFetchingNextPage) {
          return;
        }
        void listQuery.fetchNextPage();
      },
      { root, rootMargin: "240px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [listQuery.fetchNextPage, listQuery.hasNextPage, listQuery.isFetchingNextPage, rows.length]);

  const breadcrumbLabel = selectedListRow?.fs_account_name ?? title;

  function openAccount(fsAccountId: number): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("fs_account_id", String(fsAccountId));
      return next;
    });
  }

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((left, right) => {
      const leftValue =
        sortKey === "owner" ? (left.owner_rep?.display_name ?? "") : left.fs_account_name;
      const rightValue =
        sortKey === "owner" ? (right.owner_rep?.display_name ?? "") : right.fs_account_name;
      const compared = leftValue.localeCompare(rightValue, undefined, { sensitivity: "base" });
      return sortDir === "asc" ? compared : -compared;
    });
    return copy;
  }, [rows, sortDir, sortKey]);

  const clientColumns = useMemo((): HubDataTableColumn<InsideSalesClientListRow, ClientSortKey>[] => {
    return [
      {
        id: "account",
        header: "Account",
        sortKey: "account",
        sortValue: (row) => row.fs_account_name,
        cell: (row) => <span className="text-xs font-medium text-slate-900">{row.fs_account_name}</span>,
      },
      {
        id: "owner",
        header: "Owner",
        sortKey: "owner",
        sortValue: (row) => row.owner_rep?.display_name ?? "",
        cell: (row) =>
          row.owner_rep != null ? <SalesRepBadge rep={row.owner_rep} /> : "—",
      },
    ];
  }, []);

  function toggleClientSort(key: ClientSortKey): void {
    if (key === sortKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  }

  const listCountLabel =
    totalCount != null ? (
      <span className="font-normal text-slate-600">({String(totalCount)})</span>
    ) : null;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={help} breadcrumbLabel={breadcrumbLabel}>
        {title}
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="customers-search">
              {searchLabel}
            </label>
            <Input
              id="customers-search"
              type="search"
              className="h-10 max-w-xl"
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        <HubDataTable
          className="min-h-[28rem]"
          title={
            <>
              Customers {listCountLabel}
            </>
          }
          headerExtra={
            listQuery.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden />
            ) : null
          }
          columns={clientColumns}
          rows={sortedRows}
          rowKey={(row) => row.fs_account_id}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={toggleClientSort}
          loading={listQuery.isLoading}
          loadingMessage="Loading…"
          error={listQuery.isError ? listQuery.error.message : null}
          empty="No customers."
          isFiltered={debouncedSearch.trim() !== ""}
          filteredEmpty="No rows match this search."
          onRowClick={(row) => openAccount(row.fs_account_id)}
          rowClassName={(row) =>
            cn(
              selectedFsAccountId === row.fs_account_id && "bg-sky-50 ring-1 ring-inset ring-sky-200",
            )
          }
          scrollRegionRef={listScrollRef}
          listScrollFooter={
            rows.length > 0 ? (
              <>
                <div ref={loadMoreSentinelRef} className="h-4" aria-hidden />
                {listQuery.isFetchingNextPage ? (
                  <p className="px-3 py-2 text-xs text-slate-500">Loading more customers…</p>
                ) : null}
                {totalCount != null && rows.length < totalCount && !listQuery.isFetchingNextPage ? (
                  <p className="px-3 py-2 text-xs text-slate-500">
                    Showing {rows.length} of {totalCount}
                  </p>
                ) : null}
              </>
            ) : null
          }
          tableClassName="w-full table-fixed border-collapse text-left text-xs text-slate-800"
        />

        <Card className="flex min-h-0 flex-col">
          {selectedFsAccountId == null ? (
            <CardBody className="flex flex-1 flex-col justify-center">
              <p className="text-sm text-slate-600">{emptySelectionMessage}</p>
            </CardBody>
          ) : (
            <>
              <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <CardTitle>
                    {detailQuery.data?.row.fs_account_name != null
                      ? String(detailQuery.data.row.fs_account_name)
                      : selectedListRow?.fs_account_name ?? "Details"}
                  </CardTitle>
                  {(() => {
                    const ownerRep = parseRepBadge(detailQuery.data?.row.owner_rep);
                    return ownerRep != null ? <SalesRepBadge rep={ownerRep} /> : null;
                  })()}
                </div>
                {detailQuery.isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden />
                ) : null}
              </CardHeader>
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4">
                <div className="flex min-w-0 flex-wrap gap-1" role="tablist" aria-label="Customer">
                  {CUSTOMER_DETAIL_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={detailTab === tab.key}
                      className={cn(
                        "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                        detailTab === tab.key
                          ? "border-slate-900 text-slate-900"
                          : "border-transparent text-slate-600 hover:text-slate-900",
                      )}
                      onClick={() => setDetailTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {detailTab === "complaints" || detailTab === "rmas" ? (
                  <Button type="button" onClick={() => setCreateKind(detailTab === "complaints" ? "complaint" : "rma")}>
                    {detailTab === "complaints" ? "New complaint" : "New RMA"}
                  </Button>
                ) : null}
              </div>
              <CardBody className={detailTab === "details" ? "min-h-0 flex-1 overflow-y-auto" : "flex min-h-0 flex-1 flex-col overflow-hidden p-0"}>
                {detailTab === "details" ? (
                  <>
                    {detailQuery.isError ? (
                      <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                        {detailQuery.error.message}
                      </p>
                    ) : null}
                    {detailQuery.data != null ? (
                      <ClientDetailPanel
                        row={detailQuery.data.row}
                        freshsalesFields={detailQuery.data.freshsales_fields}
                        freshdeskCompany={
                          detailQuery.data.freshdesk_company == null
                            ? null
                            : {
                                id: detailQuery.data.freshdesk_company.freshdeskCompanyId,
                                name: detailQuery.data.freshdesk_company.freshdeskCompanyName,
                              }
                        }
                        freshdeskSaving={saveFreshdeskCompany.isPending}
                        onFreshdeskCompanyChange={(company) => saveFreshdeskCompany.mutate(company?.id ?? null)}
                      />
                    ) : null}
                    {detailQuery.isLoading ? <p className="text-sm text-slate-600">Loading details…</p> : null}
                  </>
                ) : detailTab === "tickets" ? (
                  <CustomerTicketsPanel query={ticketsQuery} />
                ) : (
                  <CustomerRelatedPanel tab={detailTab} query={relatedQuery} />
                )}
              </CardBody>
            </>
          )}
        </Card>
      </div>
      {createKind === "complaint" && selectedFsAccountId != null ? (
        <CustomerNewComplaintModal
          companyId={selectedFsAccountId}
          pending={createComplaint.isPending}
          error={createComplaint.error instanceof Error ? createComplaint.error.message : null}
          onClose={() => {
            createComplaint.reset();
            setCreateKind(null);
          }}
          onSubmit={(input) => createComplaint.mutate(input)}
        />
      ) : null}
      {createKind === "rma" && selectedFsAccountId != null ? (
        <CustomerNewRmaModal
          defaultCompanyId={selectedFsAccountId}
          pending={createRma.isPending}
          error={createRma.error instanceof Error ? createRma.error.message : null}
          onClose={() => {
            createRma.reset();
            setCreateKind(null);
          }}
          onSubmit={(input) => createRma.mutate(input)}
        />
      ) : null}
    </div>
  );
}
